import {Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {defaultStammdatenResource, StammdatenResource} from '@services/resource/registration-resource';
import {COUNTRIES, Country} from '@app/signup/countries';
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';

import {BehaviorSubject, Observable} from 'rxjs';
import {cleanPhoneCountryCode} from '@util/data-telephone';
import {Title} from '@services/resource/title';
import {MarkantElementsModule, MarkantInput} from '@markant/elements-angular';
import {glnChecksumValidator, GLN_VALIDATION_ERRORS} from '@util/validators/glnCecksumValidator';
import {nameValidator, NAME_VALIDATION_ERRORS} from '@util/validators/nameValidator';
import {STAMMDATEN_FORM_FIELDS} from './stammdaten-form-fields';
import {RestrictInputDirective} from '@util/directives/restrict-input.directive';
import {CommonModule} from '@angular/common';

export const STARTUP_NAME_LABEL = 'Name Deines Start-ups';

const ANGULAR_VALIDATION_ERRORS = {
  PATTERN: 'pattern',
  REQUIRED: 'required',
  MAX_LENGTH: 'maxlength',
} as const;

const glnPatternValidationErrorMessage = 'Die GLN muss 13-stellig sein und darf nur Ziffern enthalten. ';
const glnChecksumErrorMessage = 'Die Prüfziffer der GLN ist ungültig. ';
const pflichtfeldMessage = 'Pflichtfeld';
const phoneMinDigits = 6;
const phoneMaxDigits = 15;
const streetMaxLength = 64;

@Component({
  selector: 'app-stammdaten',
  templateUrl: './stammdaten.component.html',
  styleUrls: ['./stammdaten.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MarkantElementsModule, RestrictInputDirective],
})
export class StammdatenComponent implements OnInit {
  STARTUP_NAME_LABEL = STARTUP_NAME_LABEL;
  Title = Title;

  emailRegex = '^[-0-9a-zA-Z.+_]+@[-0-9a-zA-Z.+_]+\\.[a-zA-Z]+$';
  glnRegex = '^[0-9]{13}$';

  @Input() initialValue: StammdatenResource = defaultStammdatenResource;
  @Input() emailEnabled = true;
  @Output() notFulfilledRequirements = new EventEmitter<string>();

  @Output()
  value: BehaviorSubject<StammdatenResource> = new BehaviorSubject(this.initialValue);

  @ViewChildren('required') requiredElements: QueryList<MarkantInput>;
  @ViewChild('gln') glnInput: MarkantInput;

  countries: Country[] = COUNTRIES.filter((c) => c.european).sort((c1, c2) => c1.nameDe.localeCompare(c2.nameDe));

  public signupForm: UntypedFormGroup;

  ngOnInit() {
    this.signupForm = new UntypedFormGroup({
      name: new UntypedFormControl(this.initialValue.name, []),
      nickname: new UntypedFormControl(this.initialValue.nickname, []),
      street: new UntypedFormControl(this.initialValue.street, [Validators.required, Validators.maxLength(streetMaxLength)]),
      zipCode: new UntypedFormControl(this.initialValue.zipCode, []),
      city: new UntypedFormControl(this.initialValue.city, []),
      companyPhoneCountryCode: new UntypedFormControl(this.initialValue.companyPhoneCountryCode, [Validators.required]),
      companyPhone: new UntypedFormControl(this.initialValue.companyPhone, [
        Validators.required,
        this.phoneNumberValidator(STAMMDATEN_FORM_FIELDS.COMPANY_PHONE_COUNTRY_CODE),
      ]),
      countryISOCode: new UntypedFormControl(this.initialValue.countryISOCode, []),
      gln: new UntypedFormControl(this.initialValue.gln, [Validators.pattern(this.glnRegex), Validators.required, glnChecksumValidator()]),
      title: new UntypedFormControl(this.initialValue.title, [Validators.required]),
      lastName: new UntypedFormControl(this.initialValue.lastName, [Validators.required, nameValidator()]),
      firstName: new UntypedFormControl(this.initialValue.firstName, [Validators.required, nameValidator()]),
      email: new UntypedFormControl({value: this.initialValue.email, disabled: !this.emailEnabled}),
      contactPersonPhoneCountryCode: new UntypedFormControl(this.initialValue.contactPersonPhoneCountryCode, [Validators.required]),
      contactPersonPhone: new UntypedFormControl(this.initialValue.contactPersonPhone, [
        Validators.required,
        this.phoneNumberValidator(STAMMDATEN_FORM_FIELDS.CONTACT_PERSON_PHONE_COUNTRY_CODE),
      ]),
    });

    this.signupForm.controls.companyPhoneCountryCode.valueChanges.subscribe(() => {
      this.signupForm.controls.companyPhone.updateValueAndValidity({emitEvent: false});
    });

    this.signupForm.controls.contactPersonPhoneCountryCode.valueChanges.subscribe(() => {
      this.signupForm.controls.contactPersonPhone.updateValueAndValidity({emitEvent: false});
    });

    this.signupForm.valueChanges.subscribe(() => {
      const rr: StammdatenResource = this.signupForm.value;
      if (this.signupForm.controls.companyPhoneCountryCode.enabled) {
        rr.companyPhoneCountryCode = cleanPhoneCountryCode(rr.companyPhoneCountryCode);
      }
      if (this.signupForm.controls.contactPersonPhoneCountryCode.enabled) {
        rr.contactPersonPhoneCountryCode = cleanPhoneCountryCode(rr.contactPersonPhoneCountryCode);
      }
      if (!rr.contactPersonPhone) {
        rr.contactPersonPhone = rr.companyPhone;
        rr.contactPersonPhoneCountryCode = rr.companyPhoneCountryCode;
      }
      if (!rr.nickname) {
        rr.nickname = rr.name;
      }

      Object.keys(rr).forEach((k) => {
        if (typeof (rr as any)[k] === 'string') {
          (rr as any)[k] = ((rr as any)[k] as string).trim();
        }
      });

      this.value.next(rr);
    });
  }

  /**  This will set all controls as touched. Use this to trigger the highlighting
   * of missing fields from outside the form. Example: The sign-up component has
   * its own submit button, clicking that button would not trigger the
   * highlighting of required fields if those fields havne't been touched yet.
   * Therefore the sign-up component calls markAsTouched() which marks each field as touched.
   */
  public markAsTouched() {
    const keys = Object.keys(this.signupForm.controls);
    keys.forEach((k) => this.signupForm.controls[k].markAsTouched());
  }

  onCountryChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const country = this.countries.find((c) => c.iso === customEvent.detail);
    if (country) {
      this.signupForm.controls.companyPhoneCountryCode.setValue(`${country.phonecode}`);
      this.signupForm.controls.contactPersonPhoneCountryCode.setValue(`${country.phonecode}`);
    }
  }

  public validateInputs() {
    let listOfErrors = 'Folgende Felder sind fehlerhaft:\n';
    listOfErrors += this.validateRequiredMarkantInput();
    listOfErrors += this.validateGLNInput();
    this.validateNameInput();
    this.validateEMailInput();
    if (listOfErrors.includes('-')) {
      this.notFulfilledRequirements.emit(listOfErrors);
    }
  }

  private validateRequiredMarkantInput() {
    let listingOfFaultyInputs = '';
    for (const markantInput of this.requiredElements.toArray()) {
      if (!this.signupForm.get(markantInput.name!)!.valid) {
        switch (markantInput.name) {
          case STAMMDATEN_FORM_FIELDS.EMAIL: {
            this.validateEMailInput();
            break;
          }
          case STAMMDATEN_FORM_FIELDS.STREET: {
            this.applyStreetValidationHelper(markantInput);
            break;
          }
          case STAMMDATEN_FORM_FIELDS.COMPANY_PHONE:
          case STAMMDATEN_FORM_FIELDS.CONTACT_PERSON_PHONE: {
            this.applyPhoneValidationHelper(markantInput);
            break;
          }
          default: {
            this.markInputAsNotFilled(markantInput);
            break;
          }
        }
        listingOfFaultyInputs += '- ' + markantInput.label + '\n';
      } else {
        this.markInputAsFilled(markantInput);
      }
    }
    return listingOfFaultyInputs;
  }

  private validateGLNInput() {
    if (!this.signupForm.get(STAMMDATEN_FORM_FIELDS.GLN)?.valid) {
      this.glnInput.error = true;
      this.glnInput.helperPersistent = true;
      this.glnInput.helper = this.getGlnValidationErrorMessage();
      return '- GLN\n';
    } else {
      this.glnInput.error = false;
      this.glnInput.helperPersistent = false;
      this.glnInput.helper = '';
    }
    return '';
  }

  private validateNameInput() {
    for (const identifier of [STAMMDATEN_FORM_FIELDS.FIRST_NAME, STAMMDATEN_FORM_FIELDS.LAST_NAME]) {
      const control = this.signupForm.get(identifier);
      const input = this.requiredElements.toArray().find((input) => input.name === identifier);

      if (control && !control.valid && control.hasError(NAME_VALIDATION_ERRORS.NUMBER_IN_STRING) && input) {
        input.helper = 'Der Name darf keine Zahlen enthalten';
        this.markInputAsNotFilled(input);
      }
    }
  }

  private validateEMailInput() {
    const mailControl = this.signupForm.get(STAMMDATEN_FORM_FIELDS.EMAIL)!;
    let eMailInput: MarkantInput | null = null;
    for (const markantInput of this.requiredElements.toArray()) {
      if (markantInput.name === STAMMDATEN_FORM_FIELDS.EMAIL) {
        eMailInput = markantInput;
        break;
      }
    }

    if (!mailControl.valid && eMailInput) {
      if (eMailInput.value.length > 0 && mailControl.hasError(ANGULAR_VALIDATION_ERRORS.PATTERN)) {
        eMailInput.helper = 'Die E-Mail ist ungültig';
        this.markInputAsNotFilled(eMailInput);
        return false;
      } else {
        eMailInput.helper = 'Pflichtfeld';
        this.markInputAsNotFilled(eMailInput);
        return false;
      }
    } else {
      if (eMailInput) {
        this.markInputAsFilled(eMailInput);
      }
      return true;
    }
  }

  private applyPhoneValidationHelper(markantInput: MarkantInput) {
    const control = this.signupForm.get(markantInput.name!);
    if (!control) {
      return;
    }
    if (control.hasError('phoneLength') || control.hasError(ANGULAR_VALIDATION_ERRORS.PATTERN)) {
      markantInput.helper = `Telefonnummer inkl. Ländervorwahl muss ${phoneMinDigits} bis ${phoneMaxDigits} Ziffern enthalten.`;
    } else {
      markantInput.helper = pflichtfeldMessage;
    }
    this.markInputAsNotFilled(markantInput);
  }

  private applyStreetValidationHelper(markantInput: MarkantInput) {
    const control = this.signupForm.get(markantInput.name!);
    if (!control) {
      return;
    }
    if (control.hasError(ANGULAR_VALIDATION_ERRORS.MAX_LENGTH)) {
      markantInput.helper = `Maximal ${streetMaxLength} Zeichen erlaubt`;
    } else {
      markantInput.helper = pflichtfeldMessage;
    }
    this.markInputAsNotFilled(markantInput);
  }

  private phoneNumberValidator(countryCodeControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const countryControl = control.parent?.get(countryCodeControlName);
      const combined = this.combinePhoneDigits(countryControl?.value, control.value);
      if (!combined) {
        return null;
      }
      return this.isValidCombinedPhoneNumber(combined) ? null : {phoneLength: true};
    };
  }

  private combinePhoneDigits(countryCode = '', phoneNumber = ''): string {
    return `${countryCode}${phoneNumber}`;
  }

  private isValidCombinedPhoneNumber(value: string): boolean {
    const length = value.length;
    return length >= phoneMinDigits && length <= phoneMaxDigits;
  }

  private markInputAsNotFilled(markantInput: MarkantInput) {
    markantInput.error = true;
    markantInput.helperPersistent = true;
  }

  private markInputAsFilled(markantInput: MarkantInput) {
    markantInput.error = false;
    markantInput.helperPersistent = false;
  }

  get valueChanges(): Observable<StammdatenResource | null> {
    return this.value.asObservable();
  }

  getGlnValidationErrorMessage() {
    const glnControl = this.signupForm.get(STAMMDATEN_FORM_FIELDS.GLN);
    let validationMessage = '';
    if (glnControl?.hasError(ANGULAR_VALIDATION_ERRORS.PATTERN)) {
      validationMessage += glnPatternValidationErrorMessage;
    }
    if (glnControl?.hasError(ANGULAR_VALIDATION_ERRORS.REQUIRED)) {
      validationMessage += pflichtfeldMessage;
    }
    if (glnControl?.hasError(GLN_VALIDATION_ERRORS.INVALID_CHECKSUM)) {
      validationMessage += glnChecksumErrorMessage;
    }
    return validationMessage;
  }
}
